import { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCheck, FaEdit, FaEye } from 'react-icons/fa';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ExportOptions from '../../ExportOptions';
import ConfirmActionModal from '../../ConfirmDeleteModal';
import TableSkeleton from '../../TableSkeleton';
import Pagination from '../../Shared/Pagination';

const API_BASE = 'https://api.muktihospital.com/api/medicine-supplier';
const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

const SupplierModal = ({ isOpen, onClose, onSave, supplier, mode }) => {
  const [supplierData, setSupplierData] = useState({
    supplierName: '',
    supplierContact: '',
    contactPersonName: '',
    contactPersonPhone: '',
    drugLicenseNumber: '',
    address: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      // Clear form when modal is closed
      setSupplierData({
        supplierName: '',
        supplierContact: '',
        contactPersonName: '',
        contactPersonPhone: '',
        drugLicenseNumber: '',
        address: ''
      });
      setErrors({});
      return;
    }
    if (supplier && (mode === 'edit' || mode === 'view')) {
      setSupplierData({
        supplierName: supplier.supplierName || '',
        supplierContact: supplier.supplierContact || '',
        contactPersonName: supplier.contactPersonName || '',
        contactPersonPhone: supplier.contactPersonPhone || '',
        drugLicenseNumber: supplier.drugLicenseNumber || '',
        address: supplier.address || ''
      });
    } else {
      setSupplierData({
        supplierName: '',
        supplierContact: '',
        contactPersonName: '',
        contactPersonPhone: '',
        drugLicenseNumber: '',
        address: ''
      });
    }
  }, [isOpen, supplier, mode]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!supplierData.supplierName.trim()) {
      newErrors.supplierName = 'Supplier Name is required';
    }
    if (supplierData.supplierContact && !/^\+?\d{10,}$/.test(supplierData.supplierContact)) {
      newErrors.supplierContact = 'Invalid contact number';
    }
    if (supplierData.contactPersonPhone && !/^\+?\d{10,}$/.test(supplierData.contactPersonPhone)) {
      newErrors.contactPersonPhone = 'Invalid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSupplierData((prevData) => ({
      ...prevData,
      [name]: value
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors in the form", { position: "top-right", autoClose: 3000 });
      return;
    }

    setIsLoading(true);
    try {
      const url = mode === 'edit' ? `${API_BASE}/edit/${supplier.id}` : `${API_BASE}/add`;
      const method = mode === 'edit' ? 'put' : 'post';
      
      const response = await axios({
        method,
        url,
        data: supplierData,
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(`Supplier ${mode === 'edit' ? 'updated' : 'added'} successfully!`, { position: "top-right", autoClose: 3000 });
        onSave(response.data.supplier);
        onClose();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Network error! Please try again.';
      toast.error(errorMessage, { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    { name: 'supplierName', label: 'Supplier Name', required: true },
    { name: 'supplierContact', label: 'Supplier Contact' },
    { name: 'contactPersonName', label: 'Contact Person Name' },
    { name: 'contactPersonPhone', label: 'Contact Person Phone' },
    { name: 'drugLicenseNumber', label: 'Drug License Number' },
    { name: 'address', label: 'Address' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-700">
            {mode === 'view' ? 'View Supplier' : mode === 'edit' ? 'Edit Supplier' : 'Add Supplier'}
          </h2>
        </div>
        <div className="p-4 grid grid-cols-3 gap-4">
          {fields.map((field) => (
            <div key={field.name} className="col-span-2 sm:col-span-1">
              <label className="label">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {mode === 'view' ? (
                <div className="input-field bg-gray-100">{supplierData[field.name] || '-'}</div>
              ) : (
                <input
                  type="text"
                  name={field.name}
                  value={supplierData[field.name]}
                  onChange={handleChange}
                  className={`input-field ${errors[field.name] ? 'border-red-500' : ''}`}
                  placeholder={field.label}
                  disabled={isLoading}
                />
              )}
              {errors[field.name] && (
                <span className="text-red-500 text-xs mt-1">{errors[field.name]}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {mode !== 'view' && (
            <button
              onClick={handleSave}
              className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'add',
    supplier: null
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.supplierContact && supplier.supplierContact.includes(searchTerm))
  );
  
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const currentData = filteredSuppliers
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((supplier, index) => ({
      ...supplier,
      serialNumber: (currentPage - 1) * itemsPerPage + index + 1
    }));

  useEffect(() => {
      const fetchSuppliers = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(API_BASE, {
          headers: { 'x-api-key': API_KEY }
        });
        const fetchedSuppliers = response.data.suppliers || [];
        setSuppliers(fetchedSuppliers.sort((a, b) => b.id - a.id));
      } catch (error) {
        toast.error("Failed to fetch suppliers: " + (error.response?.data?.error || 'Network error'), { position: "top-right", autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchSuppliers();
  }, []);

   useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    setSelectedRows(e.target.checked ? currentData.map((supplier) => supplier.id) : []);
  };

  const handleRowSelect = (supplierId) => {
    setSelectedRows((prev) =>
      prev.includes(supplierId)
        ? prev.filter((id) => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleDelete = async (supplierIds) => {
    setIsLoading(true);
    try {
      await Promise.all(supplierIds.map(id =>
        axios.delete(`${API_BASE}/delete/${id}`, {
          headers: { 'x-api-key': API_KEY }
        })
      ));
      setSuppliers((prev) => prev.filter((supplier) => !supplierIds.includes(supplier.id)));
      setSelectedRows([]);
      toast.success(`${supplierIds.length} supplier(s) deleted successfully!`, { position: "top-right", autoClose: 3000 });
    } catch (error) {
      toast.error("Failed to delete suppliers: " + (error.response?.data?.error || 'Network error'), { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };
  const handleAddSupplier = (newSupplier) => {
    setSuppliers((prev) => [newSupplier, ...prev]);
  };

  const handleUpdateSupplier = (updatedSupplier) => {
    setSuppliers((prev) =>
      prev.map((supplier) =>
        supplier.id === updatedSupplier.id ? updatedSupplier : supplier
      )
    );
  };

  const openModal = (mode, supplier = null) => {
    setModalState({ isOpen: true, mode, supplier });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, mode: 'add', supplier: null });
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["SL No", "Supplier Name", "Supplier Contact", "Contact Person Name", "Contact Person Phone", "Drug License Number", "Address"];
  const dataMapper = (item) => [
    item.serialNumber || filteredSuppliers.indexOf(item) + 1,
    item.supplierName || '',
    item.supplierContact || '',
    item.contactPersonName || '',
    item.contactPersonPhone || '',
    item.drugLicenseNumber || '',
    item.address || ''
  ].join("\t");

 const skeletonColumns = [
  {
    width: "40px",
    height: "16px",
    className: "text-left print-hidden-in-print",
    skeletonClassName: "my-5 mx-3",
  }, // Checkbox column
  {
    width: "60px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // SL No
  {
    width: "180px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Supplier Name
  {
    width: "140px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Supplier Contact
  {
    width: "160px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Contact Person Name
  {
    width: "140px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Contact Person Phone
  {
    width: "140px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Drug License Number
  {
    width: "200px",
    height: "16px",
    className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
    skeletonClassName: "",
  }, // Address
  {
    width: "100px",
    height: "28px",
    className: "text-left p-3 print-hidden-in-print",
    skeletonClassName: "ml-auto flex gap-2",
  }, // Action (3 buttons)
];

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Supplier List</h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name or contact..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal('add')}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                disabled={isLoading}
              >
                + Add Supplier
              </button>
              <ExportOptions
                data={filteredSuppliers}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Supplier List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="overflow-x-auto print-wrapper">
            {isLoading ? (
              <div className="print-hidden">
                <table className="min-w-[1024px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">SL No</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Supplier Name</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Supplier Contact</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact Person Name</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact Person Phone</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Drug License Number</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Address</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : currentData.length === 0 ? (
              <div className="p-4 text-center font-inter text-M-heading-color print-hidden">No suppliers found</div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[1024px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={selectedRows.length === currentData.length && currentData.length > 0}
                            disabled={isLoading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label htmlFor="agreement" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">SL No</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Supplier Name</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Supplier Contact</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact Person Name</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Contact Person Phone</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Drug License Number</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Address</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((supplier, index) => (
                      <tr key={supplier.id} className="border-b hover:bg-gray-50">
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`supplier${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(supplier.id)}
                              onChange={() => handleRowSelect(supplier.id)}
                              disabled={isLoading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label htmlFor={`supplier${index}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.serialNumber}</td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {supplier.supplierName}
                        </td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.supplierContact || '-'}</td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.contactPersonName || '-'}</td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.contactPersonPhone || '-'}</td>
                        <td className="text-left uppercase font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.drugLicenseNumber || '-'}</td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{supplier.address || '-'}</td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print flex shrink-0">
                          <button
                            onClick={() => openModal('view', supplier)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title='View'
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => openModal('edit', supplier)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title='Edit'
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([supplier.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title='Delete'
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filteredSuppliers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="supplier"
        title="Confirm Delete"
        confirmText="Delete"
        message={`Do you really want to delete ${selectedRows.length} supplier?`}
      />
      <SupplierModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onSave={modalState.mode === 'add' ? handleAddSupplier : handleUpdateSupplier}
        supplier={modalState.supplier}
        mode={modalState.mode}
      />
    </div>
  );
};

export default SupplierList;